import { ThumbsUp, ThumbsUpFilled } from "@carbon/icons-react";
import cx from "classnames";
import { type ForwardRefRenderFunction, type ReactNode, forwardRef, useMemo } from "react";

import { Button, type ButtonSize } from "../../lib/Button";
import { BasicTooltip } from "../../lib/Tooltip";
import { useButtonReducer } from "../ButtonAlpha/useButtonReducer";
import { ReactionAvatarGroup } from "./ReactionAvatarGroup";
import { ReactionPopoverContent } from "./ReactionPopoverContent";
import { type ReactionUser } from "./ReactionUser";

export interface ReactionAlphaProps {
	currentUser: unknown | null;
	reactionsValue?: unknown;
	onChangeReactions?: (reactions: ReactionUser[]) => void;

	size?: ButtonSize;
	disabled?: boolean;
	useSetter?: any;
	children?: ReactNode;
}

const ReactionAlphaBase: ForwardRefRenderFunction<HTMLButtonElement, ReactionAlphaProps> = (
	{
		currentUser,
		reactionsValue = [],
		onChangeReactions,
		size = "sm",
		disabled = false,
		useSetter,
		...props
	},
	ref
) => {
	// Since we need to do `...rest` for Radix UI triggers, let's remove any lingering properties
	// that might affect `Button`.
	delete props["label"];
	delete props["variant"];
	delete props["level"];
	delete props["loading"];
	delete props["onClick"];
	delete props["reducer"];

	const { isLoading, isDisabled } = useButtonReducer(undefined, undefined, disabled);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	useSetter && useSetter(reactionsValue);

	if (!Array.isArray(reactionsValue)) {
		reactionsValue = [];
	}

	const userLoggedIn = !!currentUser;

	const currentUserVoted = useMemo(
		() =>
			typeof currentUser === "object" &&
			currentUser &&
			"id" in currentUser &&
			Array.isArray(reactionsValue) &&
			reactionsValue.find((reaction) => reaction.id === currentUser?.id)
				? true
				: false,
		[reactionsValue, currentUser]
	);

	const use3pxBorder = size === "md" || size === "lg";

	let tooltipContent: ReactNode = Array.isArray(reactionsValue) && reactionsValue.length > 0 && (
		<ReactionPopoverContent size={size} reactionsValue={reactionsValue} />
	);
	if (!userLoggedIn) {
		tooltipContent = "Login to react!";
	}

	return (
		<BasicTooltip triggerProps={{ asChild: true }} content={tooltipContent}>
			<Button
				className={cx(
					"!rounded-lg",
					currentUserVoted && [
						"outline-dark-spectrum-blue",
						{
							"outline-[2px] -outline-offset-[2px]": !use3pxBorder,
							"outline-[3px] -outline-offset-[3px]": use3pxBorder,
						},
						"hover:!bg-light-spectrum-blue-hover",
						"focus:hover:!bg-light-spectrum-blue-focus",
						"!bg-light-spectrum-blue",
						"[&>svg]:!fill-dark-spectrum-blue",
					]
				)}
				ref={ref}
				disabled={!userLoggedIn || isDisabled}
				variant={"normal"}
				level={"secondary"}
				size={size}
				loading={isLoading}
				toggled={currentUserVoted}
				icon={currentUserVoted ? ThumbsUpFilled : ThumbsUp}
				onClick={async () => {
					if (!Array.isArray(reactionsValue)) {
						return;
					}
					if (
						currentUserVoted &&
						typeof currentUser === "object" &&
						currentUser &&
						"id" in currentUser
					) {
						onChangeReactions?.(
							reactionsValue.filter((reaction) => reaction.id !== currentUser?.id)
						);
						return;
					}
					if (!currentUser) {
						onChangeReactions?.(reactionsValue);
						return;
					}

					return onChangeReactions?.([...reactionsValue, currentUser]);
				}}
				// IMPORTANT: Spread props here or `Button` will not work with RadixUI triggers!
				{...props}
			>
				<ReactionAvatarGroup
					size={size}
					currentUser={currentUser as ReactionUser}
					reactions={reactionsValue as ReactionUser[]}
				/>
			</Button>
		</BasicTooltip>
	);
};

export const ReactionAlpha = forwardRef(ReactionAlphaBase);
